import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { DashboardImageControls } from '../../components/common/DashboardImageControls';
import { DashboardImagePreview } from '../../components/common/DashboardImagePreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { VizSimTagChip } from '../../components/common/VizSimTagChip';
import { VizSimTagEditor } from '../../components/common/VizSimTagEditor';
import { useDashboardImageSettings } from '../../hooks/useDashboardImageSettings';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ForgeSupportedChainInfo,
  VizSimDatasetCrop,
  VizSimDatasetDetailResponse,
  VizSimDatasetRelease,
  VizSimDatasetReleaseType,
  VizSimDatasetSummary,
} from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';
import { normalizeTagPart, normalizeVizSimTags, splitClassTags } from '../../utils/vizsimTags';

const DATASET_FETCH_LIMIT = 200;
const SAMPLE_PAGE_LIMIT = 30;
const SELECTED_CHAIN_PARAM = 'chain';

interface ChainDatasetRow {
  chainId: number;
  chainName: string;
  dataset: VizSimDatasetSummary | null;
}

interface VizSimClassSampleGroup {
  key: string;
  classHash: string;
  className: string;
  tags: string[];
  notes: string;
  samples: VizSimDatasetCrop[];
}

interface VizSimClassEditState {
  classHash: string;
  className: string;
  tags: string[];
  notes: string;
}

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function shortId(value: string): string {
  return value.length > 14 ? `${value.slice(0, 7)}...${value.slice(-5)}` : value;
}

function versionValue(release: VizSimDatasetRelease): string {
  return `${release.major}.${release.minor}`;
}

function parseChainIdParam(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function cropVersionLabel(sample: VizSimDatasetCrop): string {
  if (sample.firstReleasedMajor != null && sample.firstReleasedMinor != null) {
    return `first v${sample.firstReleasedMajor}.${sample.firstReleasedMinor}`;
  }
  if (sample.releaseMajor != null && sample.releaseMinor != null) {
    return `v${sample.releaseMajor}.${sample.releaseMinor}`;
  }
  if (sample.lastReleasedMajor != null && sample.lastReleasedMinor != null) {
    return `latest v${sample.lastReleasedMajor}.${sample.lastReleasedMinor}`;
  }
  return 'unreleased';
}

function vizSimCropImageUrl(sample: VizSimDatasetCrop): string | null {
  return sample.snapshot.publicVizUrl || sample.snapshot.vizUrl || null;
}

function vizSimCropZoomMeta(sample: VizSimDatasetCrop): ZoomMeta {
  return {
    id: sample.cropId,
    storeId: String(sample.snapshot.storeId),
    source: sample.snapshot.source,
    forgeOriginSource: sample.snapshot.forgeOriginSource ?? undefined,
    barcode: sample.snapshot.barcode ?? undefined,
    normalizedBarcode: sample.snapshot.normalizedBarcode,
    classificationType: sample.snapshot.classificationType,
    imageResolution: sample.snapshot.imageResolution,
    createdBy: sample.createdBy || sample.snapshot.createdBy,
    createdAt: sample.createdAt || sample.snapshot.createdAt || undefined,
    updatedBy: sample.updatedBy || sample.snapshot.updatedBy || undefined,
    updatedAt: sample.updatedAt || sample.snapshot.updatedAt || undefined,
  };
}

function orderedUniqueTags(values: string[]): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  values.forEach((value) => {
    const tag = value.trim();
    if (!tag || seen.has(tag)) return;
    seen.add(tag);
    tags.push(tag);
  });
  return tags;
}

function sameTags(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((tag, index) => tag === right[index]);
}

function sameClassEditState(left: VizSimClassEditState, right: VizSimClassEditState): boolean {
  return (
    left.classHash === right.classHash &&
    left.className === right.className &&
    sameTags(left.tags, right.tags) &&
    left.notes === right.notes
  );
}

function classMetadataForSample(sample: VizSimDatasetCrop): {
  classHash: string;
  className: string;
  regularTags: string[];
} {
  const legacy = splitClassTags(sample.tags);
  return {
    classHash: sample.classHash ?? legacy.classHash,
    className: sample.className ?? legacy.className,
    regularTags: legacy.regularTags,
  };
}

type VizSimMetricTone = 'neutral' | 'ok' | 'warning' | 'critical';

function VizSimStatusBadge({ initialized }: { initialized: boolean }) {
  return (
    <span className={`system-health-status system-health-status--${initialized ? 'ok' : 'muted'}`}>
      <span className={`forge-health-dot ${initialized ? 'healthy' : 'neutral'}`} />
      <span>{initialized ? 'Initialized' : 'Not initialized'}</span>
    </span>
  );
}

function VizSimMetric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  tone?: VizSimMetricTone;
}) {
  return (
    <div className={`system-health-metric system-health-metric--${tone}`}>
      <div className="system-health-metric-value">
        {typeof value === 'number' ? formatNumber(value) : value}
      </div>
      <div className="system-health-metric-label">{label}</div>
    </div>
  );
}

export const VizSimDatasetsDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chains, setChains] = useState<ForgeSupportedChainInfo[]>([]);
  const [datasets, setDatasets] = useState<VizSimDatasetSummary[]>([]);
  const [selectedRowChainId, setSelectedRowChainId] = useState<number | null>(null);
  const [detail, setDetail] = useState<VizSimDatasetDetailResponse | null>(null);
  const [selectedVersion, setSelectedVersion] = useState('live');
  const [samplePage, setSamplePage] = useState(1);
  const [tagFilterInput, setTagFilterInput] = useState('');
  const [appliedTagFilter, setAppliedTagFilter] = useState('');
  const [classHashFilterInput, setClassHashFilterInput] = useState('');
  const [appliedClassHashFilter, setAppliedClassHashFilter] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [releaseType, setReleaseType] = useState<VizSimDatasetReleaseType>('minor');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [isReleasing, setIsReleasing] = useState(false);
  const [editingGroup, setEditingGroup] = useState<VizSimClassSampleGroup | null>(null);
  const [editInitialState, setEditInitialState] = useState<VizSimClassEditState | null>(null);
  const [editClassHash, setEditClassHash] = useState('');
  const [editClassName, setEditClassName] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editNotes, setEditNotes] = useState('');
  const [isSavingSample, setIsSavingSample] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const detailRequestIdRef = useRef(0);
  const imageSettings = useDashboardImageSettings('forge-vizsim-datasets-crop-size', 46);
  const selectedChainIdFromUrl = useMemo(
    () => parseChainIdParam(searchParams.get(SELECTED_CHAIN_PARAM)),
    [searchParams]
  );

  const selectChainRow = useCallback(
    (chainId: number) => {
      setSelectedRowChainId(chainId);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(SELECTED_CHAIN_PARAM, String(chainId));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const datasetByChainId = useMemo(
    () => new Map(datasets.map((dataset) => [dataset.chainId, dataset])),
    [datasets]
  );

  const chainRows = useMemo<ChainDatasetRow[]>(() => {
    const knownChainIds = new Set(chains.map((chain) => chain.chainId));
    const supportedRows = chains.map((chain) => ({
      chainId: chain.chainId,
      chainName: chain.chainName,
      dataset: datasetByChainId.get(chain.chainId) ?? null,
    }));
    const orphanDatasetRows = datasets
      .filter((dataset) => !knownChainIds.has(dataset.chainId))
      .map((dataset) => ({
        chainId: dataset.chainId,
        chainName: `Chain ${dataset.chainId}`,
        dataset,
      }));
    return [...supportedRows, ...orphanDatasetRows].sort(
      (left, right) => left.chainId - right.chainId
    );
  }, [chains, datasetByChainId, datasets]);

  const selectedRow = useMemo(
    () => chainRows.find((row) => row.chainId === selectedRowChainId) ?? null,
    [chainRows, selectedRowChainId]
  );
  const selectedDatasetId = selectedRow?.dataset?.datasetId ?? null;
  const selectedDataset = detail?.dataset ?? selectedRow?.dataset ?? null;
  const initializedCount = chainRows.filter((row) => row.dataset).length;
  const totalLiveSamples = chainRows.reduce(
    (total, row) => total + (row.dataset?.liveSampleCount ?? 0),
    0
  );
  const totalReleasedSamples = chainRows.reduce(
    (total, row) => total + (row.dataset?.releasedSampleCount ?? 0),
    0
  );
  const totalDraftSamples = chainRows.reduce(
    (total, row) => total + (row.dataset?.draftSampleCount ?? 0),
    0
  );
  const sampleGroups = useMemo<VizSimClassSampleGroup[]>(() => {
    const groups = new Map<string, VizSimClassSampleGroup>();
    (detail?.samples ?? []).forEach((sample) => {
      const { classHash, className, regularTags } = classMetadataForSample(sample);
      const key =
        classHash || className ? `${classHash || '-'}:${className || '-'}` : 'unclassified';
      const existingGroup = groups.get(key);
      if (existingGroup) {
        existingGroup.samples.push(sample);
        existingGroup.tags = orderedUniqueTags([...existingGroup.tags, ...regularTags]);
        if (!existingGroup.notes && sample.notes) existingGroup.notes = sample.notes;
        return;
      }
      groups.set(key, {
        key,
        classHash,
        className,
        tags: regularTags,
        notes: sample.notes ?? '',
        samples: [sample],
      });
    });
    return [...groups.values()];
  }, [detail?.samples]);
  const editState = useMemo<VizSimClassEditState>(
    () => ({
      classHash: normalizeTagPart(editClassHash),
      className: editClassName.trim(),
      tags: normalizeVizSimTags(editTags),
      notes: editNotes.trim(),
    }),
    [editClassHash, editClassName, editTags, editNotes]
  );
  const isEditDirty = useMemo(() => {
    if (!editInitialState) return false;
    return !sameClassEditState(editState, editInitialState);
  }, [editInitialState, editState]);

  useEffect(() => {
    if (!token) return;
    ForgeApiService.fetchSupportedChains(token)
      .then((response) => setChains(response.chains))
      .catch((error) => {
        showErrorToast(error instanceof Error ? error.message : 'Error loading supported chains');
      });
  }, [showErrorToast, token]);

  const loadDatasets = useCallback(async () => {
    if (!token) return;
    setIsLoadingList(true);
    try {
      const loadedDatasets: VizSimDatasetSummary[] = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const response = await ForgeApiService.fetchVizSimDatasets(token, {
          page,
          limit: DATASET_FETCH_LIMIT,
        });
        loadedDatasets.push(...response.datasets);
        hasNext = response.hasNext;
        page += 1;
      }
      setDatasets(loadedDatasets);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error loading VizSim datasets');
    } finally {
      setIsLoadingList(false);
    }
  }, [showErrorToast, token]);

  useEffect(() => {
    void loadDatasets();
  }, [loadDatasets]);

  useEffect(() => {
    if (selectedChainIdFromUrl && chainRows.some((row) => row.chainId === selectedChainIdFromUrl)) {
      if (selectedRowChainId !== selectedChainIdFromUrl) {
        setSelectedRowChainId(selectedChainIdFromUrl);
      }
      return;
    }
    if (selectedRowChainId && chainRows.some((row) => row.chainId === selectedRowChainId)) {
      return;
    }
    const fallbackChainId = chainRows[0]?.chainId ?? null;
    if (fallbackChainId === null) {
      setSelectedRowChainId(null);
      return;
    }
    selectChainRow(fallbackChainId);
  }, [chainRows, selectChainRow, selectedChainIdFromUrl, selectedRowChainId]);

  useEffect(() => {
    setSelectedVersion('live');
    setSamplePage(1);
    setDetail(null);
    setTagFilterInput('');
    setAppliedTagFilter('');
    setClassHashFilterInput('');
    setAppliedClassHashFilter('');
    setReleaseType('minor');
    setReleaseNotes('');
    setEditingGroup(null);
    setEditInitialState(null);
  }, [selectedDatasetId]);

  const loadDetail = useCallback(async () => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    if (!token || !selectedDatasetId) {
      setDetail(null);
      setIsLoadingDetail(false);
      return;
    }
    const [releaseMajor, releaseMinor] =
      selectedVersion === 'live' ? [null, null] : selectedVersion.split('.').map(Number);
    setIsLoadingDetail(true);
    try {
      const response = await ForgeApiService.fetchVizSimDataset(token, selectedDatasetId, {
        releaseMajor,
        releaseMinor,
        tag: appliedTagFilter || null,
        classHash: appliedClassHashFilter || null,
        samplePage,
        sampleLimit: SAMPLE_PAGE_LIMIT,
      });
      if (detailRequestIdRef.current !== requestId) return;
      setDetail(response);
    } catch (error) {
      if (detailRequestIdRef.current !== requestId) return;
      showErrorToast(error instanceof Error ? error.message : 'Error loading VizSim dataset');
    } finally {
      if (detailRequestIdRef.current === requestId) {
        setIsLoadingDetail(false);
      }
    }
  }, [
    appliedClassHashFilter,
    appliedTagFilter,
    samplePage,
    selectedDatasetId,
    selectedVersion,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const handleSampleFilterSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSamplePage(1);
    setAppliedTagFilter(tagFilterInput.trim());
    setAppliedClassHashFilter(normalizeTagPart(classHashFilterInput));
  };

  const handleInitializeChain = async (chainId: number) => {
    if (!token) return;
    setIsInitializing(true);
    try {
      const response = await ForgeApiService.createVizSimDataset(token, { chain_id: chainId });
      selectChainRow(chainId);
      setDetail(response);
      showSuccessToast(`VizSim dataset initialized for chain ${chainId}`);
      await loadDatasets();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error initializing VizSim dataset');
    } finally {
      setIsInitializing(false);
    }
  };

  const handleRelease = async () => {
    if (!token || !selectedDataset) return;
    setIsReleasing(true);
    try {
      const response = await ForgeApiService.releaseVizSimDataset(
        token,
        selectedDataset.datasetId,
        {
          release_type: releaseType,
          notes: releaseNotes.trim() || null,
        }
      );
      const released = response.selectedRelease ?? response.dataset.latestRelease;
      setDetail(response);
      setReleaseNotes('');
      if (released) setSelectedVersion(versionValue(released));
      showSuccessToast(
        `VizSim dataset released as v${released?.version ?? response.dataset.currentVersion}`
      );
      await loadDatasets();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error releasing VizSim dataset');
    } finally {
      setIsReleasing(false);
    }
  };

  const handleCopyReleasedCropIds = async () => {
    if (!token || !selectedDataset) return;
    try {
      const response = await ForgeApiService.fetchReleasedVizSimCropIds(token, {
        datasetId: selectedDataset.datasetId,
        tag: appliedTagFilter || null,
      });
      await navigator.clipboard.writeText(response.cropIds.join('\n'));
      showSuccessToast(`Copied ${formatNumber(response.total)} crop ids`);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error copying released crop ids');
    }
  };

  const handleCopyClassHash = async (classHash: string) => {
    try {
      await navigator.clipboard.writeText(classHash);
      showSuccessToast('Copied class hash');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error copying class hash');
    }
  };

  const openClassEditor = (group: VizSimClassSampleGroup) => {
    const initialState = {
      classHash: normalizeTagPart(group.classHash),
      className: group.className.trim(),
      tags: normalizeVizSimTags(group.tags),
      notes: group.notes.trim(),
    };
    setEditingGroup(group);
    setEditClassHash(group.classHash);
    setEditClassName(group.className);
    setEditTags(group.tags);
    setEditNotes(group.notes);
    setEditInitialState(initialState);
  };

  const closeClassEditor = () => {
    setEditingGroup(null);
    setEditInitialState(null);
  };

  const requestCloseClassEditor = () => {
    if (isEditDirty && !window.confirm('Discard unsaved VizSim class changes?')) return;
    closeClassEditor();
  };

  const handleSaveSample = async () => {
    if (!token || !selectedDataset || !editingGroup) return;
    const representativeSample = editingGroup.samples[0];
    if (!representativeSample) return;
    if (!editState.classHash) {
      showErrorToast('Class hash is required');
      return;
    }
    setIsSavingSample(true);
    try {
      // The backend applies this representative crop mutation to the whole live class.
      await ForgeApiService.updateVizSimDatasetCrop(
        token,
        selectedDataset.datasetId,
        representativeSample.cropId,
        {
          class_hash: editState.classHash,
          class_name: editState.className || null,
          tags: editState.tags,
          notes: editState.notes || null,
        }
      );
      closeClassEditor();
      showSuccessToast('VizSim class metadata updated');
      await loadDetail();
      await loadDatasets();
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating VizSim class metadata'
      );
    } finally {
      setIsSavingSample(false);
    }
  };

  const handleEditDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      requestCloseClassEditor();
      return;
    }
    if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    event.preventDefault();
    if (!isSavingSample) void handleSaveSample();
  };

  const handleRemoveClass = async (group: VizSimClassSampleGroup) => {
    if (!token || !selectedDataset) return;
    const representativeSample = group.samples[0];
    if (!representativeSample) return;
    const label = group.className || group.classHash || 'this class';
    if (!window.confirm(`Remove ${label} from this VizSim dataset?`)) return;
    setRemovingKey(`class:${group.key}`);
    try {
      const response = await ForgeApiService.removeVizSimDatasetCrop(
        token,
        selectedDataset.datasetId,
        representativeSample.cropId,
        'class'
      );
      showSuccessToast(
        `Removed ${formatNumber(response.removedCount)} crop${response.removedCount === 1 ? '' : 's'}`
      );
      await loadDetail();
      await loadDatasets();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error removing VizSim class');
    } finally {
      setRemovingKey(null);
    }
  };

  const handleRemoveCrop = async (group: VizSimClassSampleGroup, sample: VizSimDatasetCrop) => {
    if (!token || !selectedDataset) return;
    if (group.samples.length <= 2) {
      showErrorToast('Cannot remove a crop when it would leave fewer than two crops in the class');
      return;
    }
    if (!window.confirm('Remove this crop from the VizSim class?')) return;
    setRemovingKey(`crop:${sample.cropId}`);
    try {
      await ForgeApiService.removeVizSimDatasetCrop(
        token,
        selectedDataset.datasetId,
        sample.cropId,
        'crop'
      );
      showSuccessToast('Removed crop from VizSim class');
      await loadDetail();
      await loadDatasets();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error removing VizSim crop');
    } finally {
      setRemovingKey(null);
    }
  };

  return (
    <div className="page-content page-content--wide rdc-dashboard-page system-health-page vizsim-dashboard-page">
      <section className="forge-card rdc-dashboard-hero system-health-hero">
        <div className="system-health-hero-grid">
          <div>
            <div className="rdc-dashboard-title">VizSim Datasets</div>
          </div>
        </div>
        <div className="system-health-summary-grid">
          <VizSimMetric
            label="Initialized chains"
            value={`${formatNumber(initializedCount)}/${formatNumber(chainRows.length)}`}
            tone={initializedCount ? 'ok' : 'neutral'}
          />
          <VizSimMetric label="Current crops" value={totalLiveSamples} tone="ok" />
          <VizSimMetric label="Released crops" value={totalReleasedSamples} />
          <VizSimMetric
            label="Unreleased crops"
            value={totalDraftSamples}
            tone={totalDraftSamples ? 'warning' : 'neutral'}
          />
        </div>
      </section>

      <section className="forge-card rdc-dashboard-card system-health-card">
        <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
          <div className="system-health-card-title-left">
            <span>Chain Datasets</span>
            <span className="system-health-muted">{formatNumber(chainRows.length)} chains</span>
          </div>
          <div className="system-health-controls">
            <button
              type="button"
              className={`system-health-refresh-button system-health-refresh-button--neutral ${
                isLoadingList ? 'is-refreshing' : ''
              }`}
              onClick={() => void loadDatasets()}
              disabled={isLoadingList}
            >
              {isLoadingList ? <span className="system-health-spinner" /> : <RefreshOutlinedIcon />}
              <span>{isLoadingList ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
        <div className="system-health-table-wrap">
          <table className="system-health-chain-table vizsim-system-table">
            <colgroup>
              <col className="vizsim-col-chain" />
              <col className="vizsim-col-status" />
              <col className="vizsim-col-version" />
              <col className="vizsim-col-count" />
              <col className="vizsim-col-count" />
              <col className="vizsim-col-updated" />
            </colgroup>
            <thead>
              <tr>
                <th>Chain</th>
                <th>Status</th>
                <th className="system-health-number-cell">Version</th>
                <th className="system-health-number-cell">Current</th>
                <th className="system-health-number-cell">Unreleased</th>
                <th>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {chainRows.map((row) => (
                <tr
                  key={row.chainId}
                  className={selectedRowChainId === row.chainId ? 'is-selected' : ''}
                  onClick={() => selectChainRow(row.chainId)}
                >
                  <td>
                    <button
                      type="button"
                      className="vizsim-chain-select-button"
                      aria-label={`Select ${row.chainName} VizSim dataset`}
                      aria-pressed={selectedRowChainId === row.chainId}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectChainRow(row.chainId);
                      }}
                    >
                      <span className="system-health-chain-name">{row.chainName}</span>
                      <span className="system-health-chain-meta">chain {row.chainId}</span>
                    </button>
                  </td>
                  <td>
                    <VizSimStatusBadge initialized={Boolean(row.dataset)} />
                  </td>
                  <td className="system-health-number-cell">
                    {row.dataset ? `v${row.dataset.currentVersion}` : '-'}
                  </td>
                  <td className="system-health-number-cell">
                    {formatNumber(row.dataset?.liveSampleCount)}
                  </td>
                  <td className="system-health-number-cell">
                    {formatNumber(row.dataset?.draftSampleCount)}
                  </td>
                  <td>{formatTimestamp(row.dataset?.updatedAt)}</td>
                </tr>
              ))}
              {!chainRows.length && !isLoadingList ? (
                <tr className="vizsim-empty-row">
                  <td colSpan={6}>No chains available.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="forge-card rdc-dashboard-card system-health-card">
        {selectedRow?.dataset && selectedDataset ? (
          <>
            <div className="system-health-detail-header">
              <div>
                <div className="forge-card-title rdc-dashboard-card-title">
                  <NewReleasesOutlinedIcon />
                  {selectedRow.chainName}
                </div>
                <div className="system-health-detail-meta">
                  Chain {selectedRow.chainId} - initialized{' '}
                  {formatTimestamp(selectedDataset.createdAt)}
                </div>
              </div>
              <div className="system-health-controls">
                <VizSimStatusBadge initialized />
                <button
                  type="button"
                  className="system-health-refresh-button system-health-refresh-button--neutral"
                  disabled={!selectedDataset.releaseCount}
                  onClick={handleCopyReleasedCropIds}
                >
                  <ContentCopyOutlinedIcon />
                  <span>Crop IDs</span>
                </button>
              </div>
            </div>

            <div className="system-health-selected-summary-grid vizsim-selected-summary-grid">
              <VizSimMetric
                label="Current crops"
                value={selectedDataset.liveSampleCount}
                tone="ok"
              />
              <VizSimMetric
                label={`Released v${selectedDataset.currentVersion}`}
                value={selectedDataset.releasedSampleCount}
              />
              <VizSimMetric
                label="Unreleased crops"
                value={selectedDataset.draftSampleCount}
                tone={selectedDataset.draftSampleCount ? 'warning' : 'neutral'}
              />
              <VizSimMetric label="Releases" value={selectedDataset.releaseCount} />
            </div>

            <div className="system-health-section-panel vizsim-control-panel">
              <div className="system-health-section-header">
                <div>
                  <div className="system-health-section-title">Dataset releases</div>
                  <div className="system-health-section-subtitle">
                    Current editable set and released snapshots
                  </div>
                </div>
              </div>
              <div className="vizsim-section-body">
                <div className="vizsim-release-bar">
                  <label className="vizsim-field vizsim-field--inline">
                    <span>View</span>
                    <select
                      value={selectedVersion}
                      onChange={(event) => {
                        setSelectedVersion(event.target.value);
                        setSamplePage(1);
                      }}
                    >
                      <option value="live">Current editable set</option>
                      {selectedDataset.releases.map((release) => (
                        <option key={versionValue(release)} value={versionValue(release)}>
                          v{release.version} - {formatNumber(release.sampleCount)} crops
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="vizsim-field vizsim-field--inline">
                    <span>Next version</span>
                    <select
                      value={releaseType}
                      onChange={(event) =>
                        setReleaseType(event.target.value as VizSimDatasetReleaseType)
                      }
                    >
                      <option value="minor">Minor</option>
                      <option value="major">Major</option>
                    </select>
                  </label>
                  <input
                    className="vizsim-release-notes"
                    value={releaseNotes}
                    placeholder="Release notes"
                    onChange={(event) => setReleaseNotes(event.target.value)}
                  />
                  <button
                    type="button"
                    className="system-health-refresh-button"
                    disabled={
                      selectedVersion !== 'live' || !selectedDataset.liveSampleCount || isReleasing
                    }
                    onClick={handleRelease}
                  >
                    {isReleasing ? (
                      <span className="system-health-spinner system-health-spinner--small" />
                    ) : (
                      <NewReleasesOutlinedIcon />
                    )}
                    <span>{isReleasing ? 'Releasing...' : 'Release current'}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="system-health-section-panel vizsim-samples-panel">
              <div className="system-health-section-header">
                <div>
                  <div className="system-health-section-title">Dataset crops</div>
                  <div className="system-health-section-subtitle">
                    {visibleRange(samplePage, SAMPLE_PAGE_LIMIT, detail?.sampleTotal ?? 0)} of{' '}
                    {formatNumber(detail?.sampleTotal ?? 0)}
                  </div>
                </div>
                {isLoadingDetail ? (
                  <span className="system-health-status system-health-status--refreshing">
                    <span className="system-health-spinner system-health-spinner--small" />
                    <span>Loading</span>
                  </span>
                ) : null}
                <DashboardImageControls
                  label="VizSim dataset"
                  cropSize={imageSettings.cropSize}
                  onCropSizeChange={imageSettings.setCropSize}
                  zoomEnabled={imageSettings.zoomEnabled}
                  onToggleZoom={imageSettings.toggleZoom}
                />
              </div>
              <div className="vizsim-section-body">
                <form className="vizsim-sample-filter-bar" onSubmit={handleSampleFilterSubmit}>
                  <label className="vizsim-field vizsim-field--inline">
                    <span>Tag</span>
                    <input
                      value={tagFilterInput}
                      placeholder="orientation:front"
                      onChange={(event) => setTagFilterInput(event.target.value)}
                    />
                  </label>
                  <label className="vizsim-field vizsim-field--inline">
                    <span>Class ID</span>
                    <input
                      value={classHashFilterInput}
                      placeholder="abc123"
                      onChange={(event) => setClassHashFilterInput(event.target.value)}
                    />
                  </label>
                  <button type="submit" className="system-health-refresh-button">
                    <span>Filter</span>
                  </button>
                </form>
              </div>
              <div className="system-health-table-wrap">
                <table className="system-health-check-table vizsim-sample-table">
                  <thead>
                    <tr>
                      <th>Crop</th>
                      <th>Barcode</th>
                      <th>Source</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleGroups.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr className="vizsim-class-group-row">
                          <td colSpan={6}>
                            <div className="vizsim-class-group-header">
                              <div className="vizsim-class-group-main">
                                <div className="vizsim-class-identity">
                                  {group.classHash ? (
                                    <button
                                      type="button"
                                      className="vizsim-class-hash-copy"
                                      title="Copy class hash"
                                      aria-label={`Copy class hash ${group.classHash}`}
                                      onClick={() => void handleCopyClassHash(group.classHash)}
                                    >
                                      {group.classHash}
                                    </button>
                                  ) : null}
                                  {group.className ? (
                                    <MasterLabelLink
                                      to={buildMasterLabelUrl({
                                        chainId: selectedRow.chainId,
                                        cropIds: group.samples.map((sample) => sample.cropId),
                                        highlightMode: 'similar',
                                      })}
                                      className="vizsim-class-name-link"
                                      variant="forge"
                                      title="Open class crops in Master Label"
                                    >
                                      {group.className}
                                    </MasterLabelLink>
                                  ) : null}
                                  {!group.classHash && !group.className ? (
                                    <span className="system-health-muted">Unclassified</span>
                                  ) : null}
                                </div>
                                <div className="vizsim-tag-list">
                                  {group.tags.map((tag) => (
                                    <VizSimTagChip key={tag} tag={tag} />
                                  ))}
                                </div>
                              </div>
                              <div className="vizsim-class-group-actions">
                                <span className="system-health-muted">
                                  {formatNumber(group.samples.length)} crop
                                  {group.samples.length === 1 ? '' : 's'}
                                </span>
                                {selectedVersion === 'live' ? (
                                  <button
                                    type="button"
                                    className="vizsim-icon-button"
                                    aria-label={`Edit class ${group.className || group.classHash || 'unclassified'}`}
                                    onClick={() => openClassEditor(group)}
                                  >
                                    <EditOutlinedIcon />
                                  </button>
                                ) : null}
                                {selectedVersion === 'live' ? (
                                  <button
                                    type="button"
                                    className="vizsim-icon-button vizsim-icon-button--danger"
                                    aria-label={`Remove class ${group.className || group.classHash || 'unclassified'}`}
                                    disabled={removingKey !== null}
                                    onClick={() => void handleRemoveClass(group)}
                                  >
                                    <DeleteOutlineOutlinedIcon />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </td>
                        </tr>
                        {group.samples.map((sample) => (
                          <tr
                            key={`${sample.cropId}:${sample.releaseMajor ?? 'live'}:${sample.releaseMinor ?? ''}`}
                          >
                            <td>
                              <div className="vizsim-sample-cell">
                                <DashboardImagePreview
                                  imageUrl={vizSimCropImageUrl(sample)}
                                  fallback={shortId(sample.cropId)}
                                  size={imageSettings.cropSize}
                                  className="vizsim-thumb"
                                  title={sample.cropId}
                                  meta={vizSimCropZoomMeta(sample)}
                                  onImageHover={imageSettings.handleImageHover}
                                  onImageMove={imageSettings.handleImageMove}
                                  onImageLeave={imageSettings.handleImageLeave}
                                />
                                <span>{shortId(sample.cropId)}</span>
                              </div>
                            </td>
                            <td>{sample.snapshot.normalizedBarcode}</td>
                            <td>{sample.snapshot.source}</td>
                            <td>
                              <div className="vizsim-version-cell">
                                <span>{formatTimestamp(sample.createdAt)}</span>
                                <span className="vizsim-version-chip">
                                  {cropVersionLabel(sample)}
                                </span>
                              </div>
                            </td>
                            <td>{formatTimestamp(sample.updatedAt)}</td>
                            <td className="vizsim-row-action-cell">
                              {selectedVersion === 'live' ? (
                                <button
                                  type="button"
                                  className="vizsim-icon-button vizsim-icon-button--danger"
                                  aria-label={`Remove crop ${shortId(sample.cropId)}`}
                                  title={
                                    group.samples.length <= 2
                                      ? 'Cannot leave fewer than two crops in a class'
                                      : 'Remove crop from class'
                                  }
                                  disabled={
                                    group.samples.length <= 2 ||
                                    removingKey === `crop:${sample.cropId}`
                                  }
                                  onClick={() => void handleRemoveCrop(group, sample)}
                                >
                                  <DeleteOutlineOutlinedIcon />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {!detail?.samples.length && !isLoadingDetail ? (
                      <tr>
                        <td colSpan={6}>No crops in this dataset view.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="vizsim-pagination">
                <span className="system-health-muted">
                  {visibleRange(samplePage, SAMPLE_PAGE_LIMIT, detail?.sampleTotal ?? 0)} of{' '}
                  {formatNumber(detail?.sampleTotal ?? 0)}
                </span>
                <button
                  type="button"
                  className="system-health-refresh-button system-health-refresh-button--neutral"
                  disabled={samplePage <= 1}
                  onClick={() => setSamplePage((current) => Math.max(1, current - 1))}
                >
                  <span>Previous</span>
                </button>
                <button
                  type="button"
                  className="system-health-refresh-button system-health-refresh-button--neutral"
                  disabled={!detail?.sampleHasNext}
                  onClick={() => setSamplePage((current) => current + 1)}
                >
                  <span>Next</span>
                </button>
              </div>
            </div>
          </>
        ) : selectedRow ? (
          <>
            <div className="system-health-detail-header">
              <div>
                <div className="forge-card-title rdc-dashboard-card-title">
                  <NewReleasesOutlinedIcon />
                  {selectedRow.chainName}
                </div>
                <div className="system-health-detail-meta">
                  Chain {selectedRow.chainId} has not been initialized.
                </div>
              </div>
              <VizSimStatusBadge initialized={false} />
            </div>
            <div className="system-health-empty vizsim-empty-state">
              <div>No VizSim dataset has been initialized for this chain.</div>
              <button
                type="button"
                className="system-health-refresh-button"
                disabled={isInitializing}
                onClick={() => void handleInitializeChain(selectedRow.chainId)}
              >
                {isInitializing ? (
                  <span className="system-health-spinner system-health-spinner--small" />
                ) : (
                  <AddOutlinedIcon />
                )}
                <span>{isInitializing ? 'Initializing...' : 'Initialize chain'}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="system-health-empty">Select a chain dataset.</div>
        )}
      </section>
      {editingGroup ? (
        <div className="vizsim-modal-backdrop" role="presentation">
          <div
            className="vizsim-dialog vizsim-edit-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Edit VizSim class metadata"
            onKeyDown={handleEditDialogKeyDown}
          >
            <div className="vizsim-dialog-header">
              <div>
                <div className="dg-panel-title">
                  <EditOutlinedIcon />
                  Edit class metadata
                </div>
                <div className="vizsim-muted">
                  {formatNumber(editingGroup.samples.length)} crop
                  {editingGroup.samples.length === 1 ? '' : 's'}
                </div>
              </div>
              <button
                type="button"
                className="dg-secondary-button"
                onClick={requestCloseClassEditor}
              >
                Close
              </button>
            </div>

            <div className="vizsim-edit-grid">
              <label className="vizsim-field">
                <span>Class hash</span>
                <input
                  value={editClassHash}
                  placeholder="d24cf59a5040"
                  onChange={(event) => setEditClassHash(event.target.value)}
                />
              </label>
              <label className="vizsim-field">
                <span>Class name</span>
                <input
                  value={editClassName}
                  placeholder="nivea-sun-spf-60"
                  onChange={(event) => setEditClassName(event.target.value)}
                />
              </label>
            </div>

            <VizSimTagEditor
              tags={editTags}
              existingTags={detail?.existingTags ?? []}
              onChange={(nextTags) => setEditTags(nextTags)}
            />

            <label className="vizsim-field">
              <span>Notes</span>
              <textarea
                value={editNotes}
                rows={2}
                onChange={(event) => setEditNotes(event.target.value)}
              />
            </label>

            <div className="vizsim-dialog-actions">
              <button
                type="button"
                className="system-health-refresh-button"
                disabled={isSavingSample || !editState.classHash}
                onClick={() => void handleSaveSample()}
              >
                {isSavingSample ? (
                  <span className="system-health-spinner system-health-spinner--small" />
                ) : (
                  <SaveOutlinedIcon />
                )}
                <span>{isSavingSample ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {imageSettings.ZoomOverlay}
    </div>
  );
};
